import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardResponse } from '../../../types/dashboard.types';
import { dashboardService } from '../../../services/dashboardService';

interface UseDashboardDataReturn {
  data: DashboardResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Versión V2 del hook de dashboard. Reutiliza el mismo service/endpoint
 * que V1 pero sin acoplar Ant Design (los errores se exponen como estado).
 *
 * `fetchDashboard` se mantiene con identidad ESTABLE (sin `navigate` en deps,
 * accedido vía ref) para que el efecto de carga corra una sola vez en el mount
 * y no entre en un bucle de peticiones por re-renders del layout.
 */
export const useDashboardData = (): UseDashboardDataReturn => {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await dashboardService.getDashboard();
      setData(res);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Error loading the dashboard';
      if (message.includes('401') || message.includes('Unauthorized')) {
        navigateRef.current('/login');
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, refetch: fetchDashboard };
};
